import { CaptureChunk, chunkDurationMs } from "./captureSource";

/** Default retention: 5 minutes of audio (~14.4MB at 48KB/s). */
export const DEFAULT_RING_BUFFER_MS = 300_000;

/**
 * Bounded, timestamp-ordered PCM chunk buffer.
 *
 * Every captured chunk lands here before (and independently of) the socket, so
 * a rotation or reconnect can replay from any point still inside the window.
 * Eviction is purely time-based off the chunk timestamps; the controller is
 * responsible for pausing capture before eviction would eat unsent audio
 * (buffer exhaustion).
 */
export class PcmRingBuffer {
  private chunks: CaptureChunk[] = [];

  constructor(
    private readonly maxDurationMs: number = DEFAULT_RING_BUFFER_MS,
  ) {}

  push(chunk: CaptureChunk): void {
    this.chunks.push(chunk);
    const newestEnd = chunk.timestampMs + chunkDurationMs(chunk);
    while (
      this.chunks.length > 0 &&
      newestEnd - this.chunks[0].timestampMs > this.maxDurationMs
    ) {
      this.chunks.shift();
    }
  }

  get oldestTimestampMs(): number | null {
    return this.chunks.length ? this.chunks[0].timestampMs : null;
  }

  get newestEndMs(): number | null {
    if (!this.chunks.length) return null;
    const last = this.chunks[this.chunks.length - 1];
    return last.timestampMs + chunkDurationMs(last);
  }

  /**
   * Chunks whose audio ends after `fromMs`, in order. A `fromMs` that falls
   * mid-chunk includes that chunk (duplication over loss — chunk granularity
   * is 20–100ms).
   */
  sliceFrom(fromMs: number): CaptureChunk[] {
    return this.chunks.filter(
      (chunk) => chunk.timestampMs + chunkDurationMs(chunk) > fromMs,
    );
  }

  /** True when audio at `fromMs` has already been evicted. */
  isExhaustedFor(fromMs: number): boolean {
    const oldest = this.oldestTimestampMs;
    return oldest !== null && fromMs < oldest;
  }

  clear(): void {
    this.chunks = [];
  }
}
